import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Button, 
  Card, 
  CardContent, 
  useTheme, 
  Avatar,
  Paper,
  Divider,
  IconButton,
  useMediaQuery,
  Fade
} from '@mui/material';
import { 
  FaChalkboardTeacher, 
  FaUserFriends, 
  FaRegCalendarCheck,
  FaArrowRight,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaClock
} from 'react-icons/fa';
import { APP_ROUTES } from '../../utils/constants';
import api from '../../utils/api';

/**
 * Home page component with corporate design
 */
const Home = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const partnersContainerRef = useRef(null);
  const featuresRef = useRef(null);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  
  // Scroll animation state
  const [scrolled, setScrolled] = useState(false);
  // Events state
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  
  // Handle navigation
  const handleNavigation = (path) => {
    navigate(path);
  };

  const scrollToFeatures = () => {
    if (featuresRef.current) {
      featuresRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Partner organizations data
  const partners = [
    {
      id: 1,
      name: "Central Careers",
      logo: "https://res.cloudinary.com/digitaliquids-cloud-technologies/image/upload/cc_logo_sp9fpg.png",
    },
    {
      id: 2,
      name: "Africa Tech Space",
      logo: "https://res.cloudinary.com/digitaliquids-cloud-technologies/image/upload/ats-logo-1_znp9m8.png",
    },
    {
      id: 3,
      name: "Digitaliquids Cloud Technologies",
      logo: "https://res.cloudinary.com/digitaliquids-cloud-technologies/image/upload/2_xhjbtn.png",
    },
    {
      id: 4,
      name: "RVIBS",
      logo: "https://res.cloudinary.com/digitaliquids-cloud-technologies/image/upload/logoX2.fw__etmzy7.png",
    }
  ];

  // Set up auto-scrolling for partners carousel
  useEffect(() => {
    const container = partnersContainerRef.current;
    if (!container) return;

    // Clone the items to create a seamless loop
    const scrollWidth = container.scrollWidth / 2;
    let scrollPos = 0;

    const scroll = () => {
      scrollPos += 1;
      
      // Reset position when we reach the cloned section
      if (scrollPos >= scrollWidth) {
        scrollPos = 0;
        container.scrollLeft = 0;
      } else {
        container.scrollLeft = scrollPos;
      }
    };

    // Start the animation
    const timer = setInterval(scroll, 30);

    // Clean up on unmount
    return () => clearInterval(timer);
  }, []);
  
  // Add scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 100;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  // Load events for Events section
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setEventsLoading(true);
        const response = await api.get('/events');
        const data = response?.data?.data || response?.data?.events || response?.data || [];
        const list = Array.isArray(data) ? data : [];
        setEvents(list);
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setEventsLoading(false);
      }
    };
    fetchEvents();
  }, []);

  // Testimonials data
  const testimonials = [
    {
      id: 1,
      content: "We have been so confused about where our son’s education is heading especially with the expected transition to Secondary school (senior school) in 2026 and I am happy that Meira is creating a community and bringing experts to answer our questions.",
      author: "Mrs. Jebet Kwonyike",
      role: "Parent of Grade 9 Student",
      image: "https://res.cloudinary.com/digitaliquids-cloud-technologies/image/upload/user-avatar_ssixtt.png"
    },
    {
      id: 2,
      content: "The tools Meira is building, especially the Whatsapp chatbot, present a unique opportunity for us to answer the questions parents have at scale. This partnership to co-create with Meira has come at the right time.",
      author: "Mr. Victor Omondi",
      role: "Principal, Kigwa Ridge School & Montessori Centre",
      image: "https://res.cloudinary.com/digitaliquids-cloud-technologies/image/upload/user-avatar_ssixtt.png"
    },
    {
      id: 3,
      content: "Meira’s vision of democratizing information for parents through simple technologies is something that we urgently need as we help parents and their children navigate pathway selections that align with their future career aspirations. This partnership will enable us to make an impact at scale.",
      author: "Joseph Muraya",
      role: "CEO, Central Careers (Lemuel Careers Centre Limited)",
      image: "https://res.cloudinary.com/digitaliquids-cloud-technologies/image/upload/user-avatar_ssixtt.png"
    }
  ];

  // Features data
  const features = [
    {
      id: 1,
      name: "Technology & Integration",
      description: "We design and implement custom, secure solutions tailored to your environment. Through strategic partnerships with leading telecom and cloud providers, we deliver scalable, world-class platforms that are reliable and compliant.",
      icon: <FaChalkboardTeacher style={{ color: theme.palette.primary.main, fontSize: 24 }} />
    },
    {
      id: 3,
      name: "Parent Engagement",
      description: "We equip families with actionable insights and tools to raise future-ready learners. Our programs combine multi-channel communication, curated resources, and expert-led sessions—covering child development, well-being, and digital citizenship—to build a strong, informed school-parent community.",
      icon: <FaUserFriends style={{ color: theme.palette.primary.main, fontSize: 24 }} />
    },
    {
      id: 6,
      name: "Training & Capacity Building",
      description: "We upskill teachers and school leaders through practical, outcomes-focused programs: AI for teaching and school operations, cybersecurity and child online safety, and data literacy for evidence-based decisions and continuous improvement. Delivered via workshops, bootcamps, and ongoing PLC support.",
      icon: <FaRegCalendarCheck style={{ color: theme.palette.primary.main, fontSize: 24 }} />
    }
  ];

  const featureRoutes = {
    1: APP_ROUTES.SOLUTIONS_TECH_INTEGRATION,
    6: APP_ROUTES.SOLUTIONS_TRAININGS,
    3: APP_ROUTES.SOLUTIONS_PARENTAL_ENGAGEMENT
  };

  // Event helpers
  const today = new Date();
  const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  const parsedEvents = (events || [])
    .map((e) => ({ ...e, _date: new Date(e.date) }))
    .filter((e) => e._date instanceof Date && !isNaN(e._date));
  const pastEvents = parsedEvents.filter((e) => e._date < today && !isSameDay(e._date, today)).slice(0, 3);
  const presentEvents = parsedEvents.filter((e) => isSameDay(e._date, today)).slice(0, 3);
  const futureEvents = parsedEvents.filter((e) => e._date > today && !isSameDay(e._date, today)).slice(0, 3);

  // Stats data
  const stats = [
    { id: 1, name: "Families", value: "99+" },
    { id: 2, name: "Partners", value: "17+" },
    { id: 3, name: "Schools", value: "100+" },
    { id: 4, name: "Success Rate", value: "95%" }
  ];

  return (
    <>
      <Helmet>
        <title>Meira - Igniting Education pathways </title>
        <meta name="description" content="Meira Africa is building AI-powered tools that simplify access to education support for parents, teachers, and schools — driving better outcomes for every learner." />
      </Helmet>

      {/* Hero Section */}
      <Box 
        sx={{ 
          position: 'relative', 
          height: { xs: '100vh', sm: '85vh' },
          overflow: 'hidden'
        }}
      >
        {/* Hero Background with Overlay */}
        <Box 
          sx={{ 
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(0, 0, 0, 0.6)',
              zIndex: 2
            }
          }}
        >
          <Box
            component="img"
            src="https://res.cloudinary.com/digitaliquids-cloud-technologies/image/upload/blog1_1_spnatb.jpg"
            alt="Meira Education"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        </Box>
        
        {/* Hero Content */}
        <Container 
          maxWidth="lg"
          sx={{ 
            position: 'relative', 
            zIndex: 3,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            pt: { xs: 4, md: 0 }
          }}
        >
          <Grid container justifyContent="center" alignItems="center">
            <Grid item xs={12} md={10} textAlign="center">
              <Fade in={true} timeout={1000}>
                <Typography 
                  variant="h4" 
                  component="h1" 
                  fontWeight="bold"
                  sx={{ 
                    color: 'white',
                    mb: 1.6,
                    mx: 'auto',
                    maxWidth: '800px',
                    fontSize: { xs: '0.72rem', sm: '0.864rem', md: '1.056rem' }
                  }}
                >
                  Meira Africa is building AI-powered tools that simplify access to education support for parents, teachers, and schools — driving better outcomes for every learner.
                </Typography>
              </Fade>

              
              {/* Hero CTA */}
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center', gap: 0.8 }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  size="small"
                  onClick={scrollToFeatures}
                  sx={{ 
                    boxShadow: '0 1.6px 5.6px 0 rgba(0, 118, 255, 0.39)',
                    px: 1.6,
                    py: 0.6
                  }}
                >
                  Explore Our Solutions
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box sx={{ bgcolor: theme.palette.primary.main, py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Grid container justifyContent="center">
            <Grid item xs={12} md={10}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography variant="h4" component="h2" fontWeight="bold" color="white">
                  Trusted by Schools and Families
                </Typography>
                <Typography variant="h6" color="white" sx={{ opacity: 0.9, mt: 1 }}>
                  Join our growing community of parents, learners and educators
                </Typography>
              </Box>
              
              <Grid container spacing={3} justifyContent="center">
                {stats.map((stat) => (
                  <Grid item xs={6} sm={3} key={stat.id}>
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 3, 
                        textAlign: 'center',
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(4px)',
                        borderRadius: 2,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}
                    >
                      <Typography 
                        variant="h3" 
                        component="div" 
                        fontWeight="bold" 
                        color="white"
                      >
                        {stat.value}
                      </Typography>
                      <Typography variant="body1" color="white" sx={{ opacity: 0.8 }}>
                        {stat.name}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box ref={featuresRef} sx={{ py: { xs: 6, md: 10 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="overline" 
              component="div" 
              color="primary" 
              fontWeight="bold"
            >
              Features
            </Typography>
            <Typography 
              variant="h3" 
              component="h2" 
              fontWeight="bold" 
              sx={{ mb: 2 }}
            >
              Tech That Meets You Where You Are
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ 
                maxWidth: 700, 
                mx: 'auto', 
                fontSize: '1.1rem' 
              }}
            >
              Meira provides all the tools necessary for effective education in today's digital world.
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {features.map((feature) => (
              <Grid item xs={12} sm={6} md={4} key={feature.id}>
                <Card 
                  elevation={2}
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2,
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardContent sx={{ p: 3, flexGrow: 1 }}>
                    <Box 
                      onClick={featureRoutes[feature.id] ? () => handleNavigation(featureRoutes[feature.id]) : undefined}
                      role={featureRoutes[feature.id] ? 'button' : undefined}
                      aria-label={featureRoutes[feature.id] ? `${feature.name} details` : undefined}
                      sx={{ 
                        bgcolor: 'rgba(0, 118, 255, 0.1)', 
                        width: 56, 
                        height: 56, 
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                        cursor: featureRoutes[feature.id] ? 'pointer' : 'default'
                      }}
                    >
                      {feature.icon}
                    </Box>
                    
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {feature.name}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                    
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                      <IconButton 
                        size="small" 
                        color="primary"
                        sx={{ 
                          bgcolor: 'rgba(0, 118, 255, 0.1)',
                          '&:hover': {
                            bgcolor: 'rgba(0, 118, 255, 0.2)'
                          }
                        }}
                      >
                        <FaArrowRight size={16} />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Events Section */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'grey.50' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="overline" 
              component="div" 
              color="primary" 
              fontWeight="bold"
            >
              Events
            </Typography>
            <Typography 
              variant="h3" 
              component="h2" 
              fontWeight="bold" 
              sx={{ mb: 2 }}
            >
              Past, Today, and Upcoming
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ 
                maxWidth: 700, 
                mx: 'auto', 
                fontSize: '1.1rem' 
              }}
            >
              Explore events created by Meira. Join us and stay informed.
            </Typography>
          </Box>

          {eventsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <Typography variant="body1" color="text.secondary">Loading events...</Typography>
            </Box>
          ) : (
            <Grid container spacing={4}>
              {/* Today */}
              <Grid item xs={12} md={4}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Today</Typography>
                {presentEvents.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No events today.</Typography>
                ) : (
                  presentEvents.map((event) => (
                    <Paper key={event.id} elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                      {event.image && (
                        <Box component="img" src={event.image} alt={event.title} sx={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 1, mb: 1 }} />
                      )}
                      <Typography variant="subtitle1" fontWeight="bold">{event.title}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <FaCalendarAlt size={14} />
                        <Typography variant="caption">{new Date(event.date).toLocaleDateString()}</Typography>
                      </Box>
                      {event.time && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <FaClock size={14} />
                          <Typography variant="caption">{event.time}</Typography>
                        </Box>
                      )}
                      {event.location && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <FaMapMarkerAlt size={14} />
                          <Typography variant="caption">{typeof event.location === 'string' ? event.location : 'Online Event'}</Typography>
                        </Box>
                      )}
                      <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                        {event.registrationLink && (
                          <Button size="small" variant="contained" color="primary" href={event.registrationLink} target="_blank" rel="noopener noreferrer">Register</Button>
                        )}
                        <Button size="small" variant="text" onClick={() => handleNavigation(APP_ROUTES.EVENTS)}>Details</Button>
                      </Box>
                    </Paper>
                  ))
                )}
              </Grid>

              {/* Upcoming */}
              <Grid item xs={12} md={4}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Upcoming</Typography>
                {futureEvents.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No upcoming events.</Typography>
                ) : (
                  futureEvents.map((event) => (
                    <Paper key={event.id} elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                      {event.image && (
                        <Box component="img" src={event.image} alt={event.title} sx={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 1, mb: 1 }} />
                      )}
                      <Typography variant="subtitle1" fontWeight="bold">{event.title}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <FaCalendarAlt size={14} />
                        <Typography variant="caption">{new Date(event.date).toLocaleDateString()}</Typography>
                      </Box>
                      {event.time && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <FaClock size={14} />
                          <Typography variant="caption">{event.time}</Typography>
                        </Box>
                      )}
                      {event.location && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <FaMapMarkerAlt size={14} />
                          <Typography variant="caption">{typeof event.location === 'string' ? event.location : 'Online Event'}</Typography>
                        </Box>
                      )}
                      <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                        {event.registrationLink && (
                          <Button size="small" variant="contained" color="primary" href={event.registrationLink} target="_blank" rel="noopener noreferrer">Register</Button>
                        )}
                        <Button size="small" variant="text" onClick={() => handleNavigation(APP_ROUTES.EVENTS)}>Details</Button>
                      </Box>
                    </Paper>
                  ))
                )}
              </Grid>

              {/* Past */}
              <Grid item xs={12} md={4}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Past</Typography>
                {pastEvents.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No past events.</Typography>
                ) : (
                  pastEvents.map((event) => (
                    <Paper key={event.id} elevation={1} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                      {event.image && (
                        <Box component="img" src={event.image} alt={event.title} sx={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 1, mb: 1 }} />
                      )}
                      <Typography variant="subtitle1" fontWeight="bold">{event.title}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <FaCalendarAlt size={14} />
                        <Typography variant="caption">{new Date(event.date).toLocaleDateString()}</Typography>
                      </Box>
                      {event.location && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <FaMapMarkerAlt size={14} />
                          <Typography variant="caption">{typeof event.location === 'string' ? event.location : 'Online Event'}</Typography>
                        </Box>
                      )}
                      <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                        <Button size="small" variant="text" onClick={() => handleNavigation(APP_ROUTES.EVENTS)}>Details</Button>
                      </Box>
                    </Paper>
                  ))
                )}
              </Grid>
            </Grid>
          )}

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Button variant="outlined" onClick={() => handleNavigation(APP_ROUTES.EVENTS)}>View all events</Button>
          </Box>
        </Container>
      </Box>
      
      {/* Our Partners Section */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: 'grey.50' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="overline" 
              component="div" 
              color="primary" 
              fontWeight="bold"
            >
              Our Partners
            </Typography>
            <Typography 
              variant="h3" 
              component="h2" 
              fontWeight="bold" 
              sx={{ mb: 2 }}
            >
              Working Together for Education
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ 
                maxWidth: 700, 
                mx: 'auto', 
                fontSize: '1.1rem' 
              }}
            >
              Collaborating with leading organizations to transform education.
            </Typography>
          </Box>
          
          <Box 
            sx={{ 
              position: 'relative',
              overflow: 'hidden',
              mx: 'auto',
              maxWidth: '100%',
              my: 4
            }}
          >
            <Box 
              sx={{ 
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                width: 50,
                background: 'linear-gradient(to right, rgba(249, 250, 251, 1), rgba(249, 250, 251, 0))',
                zIndex: 2
              }}
            />
            
            <Box 
              sx={{ 
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                width: 50,
                background: 'linear-gradient(to left, rgba(249, 250, 251, 1), rgba(249, 250, 251, 0))',
                zIndex: 2
              }}
            />
            
            <Box 
              ref={partnersContainerRef}
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                overflowX: 'hidden',
                whiteSpace: 'nowrap',
                py: 3,
                px: 6
              }}
            >
              {[...partners, ...partners].map((partner, index) => (
                <Box 
                  key={`${partner.id}-${index}`} 
                  sx={{ 
                    display: 'inline-block',
                    flexShrink: 0
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{ 
                      p: 3,
                      bgcolor: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 80,
                      width: 160,
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.divider}`
                    }}
                  >
                    <Box
                      component="img"
                      src={partner.logo}
                      alt={partner.name}
                      sx={{
                        maxHeight: 50,
                        maxWidth: 120,
                        objectFit: 'contain'
                      }}
                    />
                  </Paper>
                </Box>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Testimonials Section */}
      <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="overline" 
              component="div" 
              color="primary" 
              fontWeight="bold"
            >
              Testimonials
            </Typography>
            <Typography 
              variant="h3" 
              component="h2" 
              fontWeight="bold" 
              sx={{ mb: 2 }}
            >
              Hear from our users
            </Typography>
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ 
                maxWidth: 700, 
                mx: 'auto', 
                fontSize: '1.1rem' 
              }}
            >
              Discover how Meira is making a difference in education across Kenya.
            </Typography>
          </Box>
          
          <Grid container spacing={4} justifyContent="center">
            {testimonials.map((testimonial) => (
              <Grid item xs={12} sm={6} md={4} key={testimonial.id}>
                <Card 
                  elevation={2}
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2,
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 4
                    },
                    position: 'relative',
                    overflow: 'visible'
                  }}
                >
                  <Box 
                    sx={{
                      position: 'absolute',
                      top: -20,
                      left: 20,
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: theme.palette.primary.main,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 24,
                      fontWeight: 'bold',
                      fontFamily: 'serif'
                    }}
                  >
                    "
                  </Box>
                  
                  <CardContent sx={{ p: 4, flexGrow: 1 }}>
                    <Typography 
                      variant="body1" 
                      paragraph 
                      sx={{ 
                        fontStyle: 'italic',
                        mb: 3
                      }}
                    >
                      "{testimonial.content}"
                    </Typography>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                      <Avatar 
                        src={testimonial.image} 
                        alt={testimonial.author}
                        sx={{ width: 48, height: 48 }}
                      />
                      <Box sx={{ ml: 2 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {testimonial.author}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {testimonial.role}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          
        </Container>
      </Box>

      {/* CTA Section */}
      <Box 
        sx={{ 
          bgcolor: theme.palette.primary.main,
          color: 'white',
          py: { xs: 6, md: 8 }
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography variant="h3" component="h2" fontWeight="bold" gutterBottom>
                Ready to step into empowered parenting?
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
                Join the Meira community and transform your educational journey.
              </Typography>
            </Grid>
            
            <Grid 
              item 
              xs={12} 
              md={5}
              sx={{ 
                display: 'flex', 
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: { xs: 'center', md: 'flex-end' }
              }}
            >
              <Button 
                component="a"
                href="https://wa.me/254757575575?text=Hello"
                target="_blank"
                rel="noopener noreferrer"
                variant="contained" 
                color="secondary" 
                size="large"
                sx={{ 
                  px: 4,
                  py: 1.5,
                  fontWeight: 'bold'
                }}
              >
                Start Now
              </Button>
              <Button 
                variant="outlined" 
                color="inherit" 
                size="large"
                onClick={() => handleNavigation(APP_ROUTES.CONTACT)}
                sx={{ 
                  px: 4,
                  py: 1.5,
                  borderColor: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                Ask Meira
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default Home;
